import { useState, useEffect, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import { recoverTokenInfo } from "@/utils/recoverTokenInfo";
import { useDeviceKey } from "@/hooks/useDeviceKey";
import { useInternetStatus } from "@/contexts/InternetStatus/useInternetStatus";

interface RecordingMessage {
  id: string;
  data: unknown;
  timestamp: Date;
}

type ConnectionStatus = "disconnected" | "connecting" | "connected";

const methodsToListen = ["RecordingUpdated", "RecordingCreated", "RecordingExportCreated", "RecordingExportUpdated"] as const;

export type RecordingHubMethod = (typeof methodsToListen)[number];

export type RecordingStatusHubPayload = {
  recordingId: string;
  status: number;
};

export type RecordingExportHubPayload = {
  recordingId: string;
  recordingExportId: string;
  exportType: number;
  status: number;
  exportedDate?: string;
  date?: string;
};

export type RecordingHubPayload = RecordingStatusHubPayload | RecordingExportHubPayload;

export type RecordingHubMessage = RecordingMessage & {
  method: RecordingHubMethod;
  data: RecordingHubPayload;
};

export const useSignalRRecordingHub = () => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("disconnected");
  const [connectionInfo, setConnectionInfo] = useState("");
  const [lastMessage, setLastMessage] = useState<RecordingHubMessage | null>(null);

  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const connectionStatusRef = useRef<ConnectionStatus>("disconnected");
  const isOnlineRef = useRef<boolean>(true);

  const tokenInfo = recoverTokenInfo();
  const deviceKey = useDeviceKey();
  const { isOnline } = useInternetStatus();
  const defaultBackendUrl = `${import.meta.env.VITE_SMART_DEBRIEF_API}`;

  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  // This is needed because Recording and RecordingExport have different payload structures
  const normalizePayload = (data: unknown): RecordingHubPayload | null => {
    const rawPayload = Array.isArray(data) ? data[0] : data;

    if (!rawPayload || typeof rawPayload !== "object") {
      return null;
    }

    if (!("recordingId" in rawPayload) || typeof rawPayload.recordingId !== "string") {
      return null;
    }

    return rawPayload as RecordingHubPayload;
  };

  // Add message to list
  const setMessage = useCallback((method: RecordingHubMethod, data: unknown) => {
    const normalizedPayload = normalizePayload(data);
    if (!normalizedPayload) {
      return;
    }

    const newMessage: RecordingHubMessage = {
      id: Math.random().toString(36).substring(2),
      method,
      data: normalizedPayload,
      timestamp: new Date(),
    };
    setLastMessage(newMessage);
  }, []);

  // Connect to SignalR hub
  const connect = useCallback(async () => {
    if (!isOnline || !defaultBackendUrl.trim() || !tokenInfo.trim()) {
      return;
    }

    if (connectionRef.current || connectionStatusRef.current === "connecting") {
      return;
    }

    const currentDeviceId = deviceKey.trim();

    setConnectionStatus("connecting");
    setConnectionInfo("Authenticating with JWT token + Device ID...");
    try {
      const hubUrl = `${defaultBackendUrl}/recordsHub?deviceId=${encodeURIComponent(currentDeviceId)}`;

      const builder = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => tokenInfo,
        })
        .withAutomaticReconnect([0, 1000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.None);

      const newConnection = builder.build();
      connectionRef.current = newConnection;

      // Keep-alive & timeout tuning
      newConnection.keepAliveIntervalInMilliseconds = 15000;
      newConnection.serverTimeoutInMilliseconds = 60000;

      // Register handlers for all methods
      methodsToListen.forEach((methodName) => {
        newConnection.on(methodName, (data) => {
          setMessage(methodName, data);
        });
      });

      // Connection lifecycle events
      newConnection.onreconnecting(() => {
        if (!isOnlineRef.current) {
          void newConnection.stop();
          return;
        }

        setConnectionStatus("connecting");
        setConnectionInfo("Attempting to reconnect...");
      });

      newConnection.onreconnected((connectionId) => {
        setConnectionStatus("connected");
        setConnectionInfo(`Connection ID: ${connectionId || "N/A"}`);
      });

      newConnection.onclose((error) => {
        if (connectionRef.current === newConnection) {
          connectionRef.current = null;
        }
        setConnectionStatus("disconnected");
        setConnectionInfo(error ? `Error: ${error.message}` : "");
      });

      await newConnection.start();

      setConnectionStatus("connected");
      setConnectionInfo(`Connection ID: ${newConnection.connectionId || "N/A"} | Device: ${currentDeviceId}`);
    } catch (error) {
      const err = error as Error & { statusCode?: number };
      connectionRef.current = null;
      setConnectionStatus("disconnected");
      setConnectionInfo(err.message);
    }
  }, [isOnline, defaultBackendUrl, tokenInfo, deviceKey, setMessage]);

  // Disconnect from SignalR hub
  const disconnect = useCallback(async () => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.stop();
      } catch (error) {
        console.error("Error while disconnecting:", error);
      }
      connectionRef.current = null;
    }

    setConnectionStatus("disconnected");
    setConnectionInfo("");
  }, []);

  const connectIfNeeded = useCallback(() => {
    if (!isOnline) {
      return;
    }

    void connect();
  }, [isOnline, connect]);

  useEffect(() => {
    if (!isOnline && connectionRef.current) {
      void disconnect();
    }
  }, [isOnline, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop().catch((error) => {
          console.error("Error while stopping connection on unmount:", error);
        });
      }
    };
  }, []);

  return {
    connectionStatus,
    connectionInfo,
    lastMessage,
    connect: connectIfNeeded,
    disconnect,
  };
};
